import { motion } from 'framer-motion';
import { ChevronDown, Sparkles, Zap, Brain } from 'lucide-react';

const floatingElements = [
  { symbol: 'Tf', name: 'Transformer', color: '#3498DB', delay: 0 },
  { symbol: 'Lm', name: 'LLM', color: '#27AE60', delay: 0.2 },
  { symbol: 'Agi', name: 'AGI', color: '#FFD700', delay: 0.4 },
  { symbol: 'Rg', name: 'RAG', color: '#1ABC9C', delay: 0.6 },
  { symbol: 'Cn', name: 'CNN', color: '#3498DB', delay: 0.8 },
  { symbol: 'At', name: 'Attention', color: '#9B59B6', delay: 1 },
];

export default function Hero() {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Animated Background */}
      <div className="absolute inset-0 cyber-grid-bg" />
      <div className="absolute inset-0 bg-gradient-to-b from-dark-950 via-transparent to-dark-950" />
      
      {/* Floating Elements */}
      <div className="absolute inset-0 pointer-events-none">
        {floatingElements.map((el, index) => (
          <motion.div
            key={el.symbol}
            className="absolute"
            style={{
              left: `${15 + (index % 3) * 30}%`,
              top: `${20 + Math.floor(index / 3) * 40}%`,
            }}
            initial={{ opacity: 0, y: 50 }}
            animate={{ 
              opacity: [0.3, 0.6, 0.3],
              y: [0, -20, 0],
            }}
            transition={{
              duration: 4,
              delay: el.delay,
              repeat: Infinity,
              repeatType: 'reverse',
            }}
          >
            <div 
              className="w-16 h-16 md:w-20 md:h-20 rounded-xl flex flex-col items-center justify-center backdrop-blur-sm border"
              style={{ 
                backgroundColor: `${el.color}20`,
                borderColor: `${el.color}40`,
              }}
            >
              <span className="text-lg md:text-xl font-bold" style={{ color: el.color }}>
                {el.symbol}
              </span>
              <span className="text-[10px] text-gray-400">{el.name}</span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Glow Effects */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyber-blue/20 rounded-full blur-[128px]" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyber-purple/20 rounded-full blur-[128px]" />

      {/* Main Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Badge */}
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Sparkles className="w-4 h-4 text-cyber-blue" />
            <span className="text-sm text-gray-300">168 Elements Across 11 Blocks</span>
          </motion.div>

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-display font-bold mb-6 tracking-tight">
            <span className="text-white">The Complete</span>
            <br />
            <span className="text-gradient">AI Periodic Table</span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto mb-8 leading-relaxed">
            A comprehensive framework for understanding Artificial Intelligence — 
            from <span className="text-foundation">Perceptrons</span> to{' '}
            <span className="text-future">Superintelligence</span>
          </p>

          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-6 md:gap-12 mb-12">
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <div className="text-3xl md:text-4xl font-bold text-gradient">168</div>
              <div className="text-sm text-gray-500">Elements</div>
            </motion.div>
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <div className="text-3xl md:text-4xl font-bold text-cyber-blue">11</div>
              <div className="text-sm text-gray-500">Blocks</div>
            </motion.div>
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <div className="text-3xl md:text-4xl font-bold text-cyber-purple">70+</div>
              <div className="text-sm text-gray-500">Years of AI</div>
            </motion.div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <motion.a
              href="#table"
              className="btn-primary flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Zap className="w-5 h-5" />
              Explore the Table
            </motion.a>
            <motion.a
              href="#learn"
              className="btn-secondary flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Brain className="w-5 h-5" />
              Learn the Framework
            </motion.a>
          </div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <a href="#table" className="flex flex-col items-center text-gray-500 hover:text-white transition-colors">
          <span className="text-xs mb-2">Scroll to explore</span>
          <ChevronDown className="w-6 h-6" />
        </a>
      </motion.div>
    </section>
  );
}
